// @dynamic-i18n-keys
//   comment.popover.unfeature
//   comment.popover.unfeature.mobile
//   comment.popover.feature
//   comment.popover.feature.mobile
//   comment.popover.block
//   comment.popover.block.mobile
//   comment.popover.report
//   comment.popover.report.mobile
import React, { useContext, useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiComment, PapiUser } from '@wix/da-papi-types';
import ddt from '@wix/da-ddt';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import { BiContext } from '@wix/da-bi/pkg/BiLogger.context';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import {
  DropdownOption,
  NullableDropdownItem,
} from '@wix/da-ds/pkg/Dropdown/types';
import { CommentContext } from '../../CommentContext';
import { isActionable } from '../../utils';
import { useButtonConditionals } from '../CommentFooter/useButtonConditionals';
import { useVerification } from '../../../VerificationPopup/withVerification';
import { BiData, CommentActionEvent } from '@wix/da-bi/pkg/events';
import s from './ActionsMenu.scss';

export interface Props {
  className?: string;
  itemClassName?: string;
  isCommentReduxReady?: boolean;
  canPostComment?: boolean;
  onReportSpam?: (comment: PapiComment) => void;
  onFeature?: (comment: PapiComment) => void;
  onUnfeature?: (comment: PapiComment) => void;
  onHide?: (comment: PapiComment, insideModal: boolean) => void;
  onUnhide?: (comment: PapiComment) => void;
  onBlockUser?: (user: PapiUser, dispatchAction?: any) => void;
}

const ActionsMenu: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  itemClassName,
  onHide,
  onUnhide,
  onReportSpam,
  onFeature,
  onUnfeature,
  onBlockUser,
  isCommentReduxReady,
  canPostComment,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { is_comment_thread } = useContext(BiContext);
  const { comment, setEditingState, extraActions, canShowActionMenu } =
    useContext(CommentContext);
  const { isFeatured, isHidden, isOwner, isAuthor, isReportable, isDeletable } =
    comment;

  const { showEditButton } = useButtonConditionals({ canPostComment });
  const { restrictToVerified } = useVerification();
  const onEdit = restrictToVerified('comment', () =>
    setEditingState('editing')
  );

  const menuItems = useMemo(() => {
    const baseBiData = BiData<CommentActionEvent>({
      evid: 180,
      itemid: comment.itemId,
      typeid: comment.typeId,
      commentid: comment.commentId,
      commenterid: comment.user?.useridUuid,
    });
    const onToggleHide = () => {
      if (!comment.isHidden) {
        onHide && onHide(comment, is_comment_thread === 1);
      } else {
        onUnhide && onUnhide(comment);
      }
    };

    let correctedExtraActions: NullableDropdownItem[] = [];
    if (extraActions) {
      correctedExtraActions = extraActions.map(
        action =>
          !!action && {
            ...action,
            className: itemClassName,
            disabled: !isCommentReduxReady,
            biData: { ...baseBiData, ...(action as DropdownOption).biData },
          }
      );
    }

    return [
      isOwner &&
        isFeatured && {
          label: t(`comment.popover.unfeature${isMobile ? '.mobile' : ''}`),
          className: itemClassName,
          disabled: !isCommentReduxReady,
          onClick: onUnfeature && (() => onUnfeature(comment)),
          biData: {
            ...baseBiData,
            menu_item_name: 'unfeature',
          },
        },
      isOwner &&
        !isFeatured && {
          label: t(`comment.popover.feature${isMobile ? '.mobile' : ''}`),
          className: itemClassName,
          disabled: !isCommentReduxReady,
          onClick: onFeature && (() => onFeature(comment)),
          biData: {
            ...baseBiData,
            menu_item_name: 'feature',
          },
        },
      isAuthor &&
        showEditButton && {
          label: t('comment.popover.edit'),
          className: itemClassName,
          disabled: !isCommentReduxReady,
          onClick: onEdit,
          biData: {
            ...baseBiData,
            menu_item_name: 'edit',
          },
        },
      isDeletable &&
        !isHidden && {
          label: t('comment.popover.delete'),
          className: itemClassName,
          disabled: !isCommentReduxReady,
          onClick: onToggleHide,
          biData: {
            ...baseBiData,
            menu_item_name: 'delete',
          },
        },
      isOwner &&
        !isAuthor && {
          label: t(`comment.popover.block${isMobile ? '.mobile' : ''}`),
          className: itemClassName,
          disabled: !isCommentReduxReady,
          onClick: () =>
            // setTimeout allows actionmenu to be hidden before confirm prompt is shown
            setTimeout(() => {
              if (
                confirm('Add this user to your block list?') &&
                onBlockUser &&
                comment.user
              ) {
                onBlockUser(comment.user);
              }
            }),
          biData: {
            ...baseBiData,
            menu_item_name: 'block',
          },
        },
      isReportable &&
        !isAuthor && {
          label: t(`comment.popover.report${isMobile ? '.mobile' : ''}`),
          className: itemClassName,
          disabled: !isCommentReduxReady,
          onClick: () =>
            // setTimeout allows actionmenu to be hidden before confirm prompt is shown
            setTimeout(() => {
              ddt.log('comments', 'reporting spam', comment);
              if (confirm('Report this comment as spam?') && onReportSpam) {
                onReportSpam(comment);
              }
            }, 100),
          biData: {
            ...baseBiData,
            menu_item_name: 'spam',
          },
        },
      ...correctedExtraActions,
    ];
  }, [
    comment,
    isAuthor,
    isCommentReduxReady,
    isDeletable,
    isFeatured,
    isHidden,
    isMobile,
    isOwner,
    isReportable,
    is_comment_thread,
    itemClassName,
    onBlockUser,
    onEdit,
    onFeature,
    onHide,
    onReportSpam,
    onUnfeature,
    onUnhide,
    showEditButton,
    extraActions,
    t,
  ]);

  if (!isActionable(comment) || !canShowActionMenu) {
    return null;
  }

  return (
    <IconButtonWithContextMenu
      className={classnames(s['actions-menu'], className)}
      placement={isMobile ? 'top-end' : 'top'}
      items={menuItems}
      aria-label={t('comment.a11y.popover.label')}
      standardMenuProps={{
        usePortal: true,
      }}
      icon={EllipsisIcon}
      size="medium"
    />
  );
};

ActionsMenu.displayName = 'ActionsMenu';

export default ActionsMenu;
